import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Button,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  TextField,
} from '@material-ui/core';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import api from 'utils/api';

import { toast, canAccess as canAccessUtil } from 'utils/helpers';
import { FormField2 as Field, CommonDialog } from 'components';

const filter = createFilterOptions();

const WorkReportModal = ({ objectId, onClose, estimateObjectId, projectId }) => {
  const { t } = useTranslation();
  const [period, setPeriod ] = useState(moment(new Date()).format('YYYY-MM'));
  const [manager, setManager ] = useState('');
  const [employees, setEmployees ] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [contractor, setContractor] = useState('');
  const [contractors, setContractors ] = useState([]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setPeriod(moment(date).format('YYYY-MM'));
  };

  const fetchEmployees = async () => {
    try {
      let contractorsAndManagersResponse = await api.get(`estimates/${estimateObjectId}/get-managers-contractors`);
      let managers = contractorsAndManagersResponse.data.managers;
      setContractors(contractorsAndManagersResponse.data.contractors);
      setEmployees(managers);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleSubmit = async () => {
    if (!manager) {
      toast(t('Įveskite darbų vadovą ir periodą'), 'error');
      return;
    }

    if (!period) {
      toast(t('Įveskite periodą darbų aktui'), 'error');
      return;
    }

    try { 
      const { data } = await api.post(`/work-reports`, { objectId, period, contractor: contractor.label, manager: manager.label });
      toast(`${t('Darbų aktas sukurtas')}`, 'success');
      window.open(`/projects/${projectId}/objects/${estimateObjectId}/workreports/${data.id}`, '_self');
    } catch (err) {
      console.error(err);
      window.alert(t('Įvyko klaida'));
    }

    onClose();
  }

  return (
    <CommonDialog
      visible
      onClose={onClose}
      maxWidth="xs"
      style={{ position: 'relative' }}
      actions={[
        <Button
          color="default"
          variant="text"
          onClick={() => onClose()}
        >
          Atšaukti
        </Button>,
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
        >
          Vykdyti
        </Button>
      ]}
    >
      <div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={2}>{t('Akto duomenys')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={{ paddingTop: 0, border: "none" }}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="yyyy/MM"
                    views={[ 'year', 'month' ]}
                    margin="dense"
                    id="date-picker-inline"
                    label={t('Periodas')}
                    value={selectedDate}
                    onChange={handleDateChange}
                    KeyboardButtonProps={{'aria-label': 'change date',}}
                    autoOk={true}
                    fullWidth
                  />
                </MuiPickersUtilsProvider>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ paddingTop: 0, border: "none" }}>
                <Autocomplete
                  value={contractor}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setContractor({
                        label: newValue,
                      });
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setContractor({
                        label: newValue.inputValue,
                      });
                    } else {
                      setContractor(newValue);
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    // Suggest the creation of a new value
                    if (params.inputValue !== '') {
                      filtered.push({
                        inputValue: params.inputValue,
                        label: `${t('Pridėti')} "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  options={_.map(contractors, (contractor) => ({ label: contractor }))}
                  freeSolo
                  style={{ marginTop: 10 }}
                  getOptionLabel={(option) => option?.label?.toString() || option?.toString()}
                  renderInput={(params) => <TextField {...params} label={t("Rangovas")} variant="outlined" />}
                  ListboxProps={{ style: { maxHeight: '15vh' } }}
                  size="small"
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell style={{ paddingTop: 0, border: "none" }}>
                <Autocomplete
                  value={manager}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setManager({
                        label: newValue,
                      });
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setManager({
                        label: newValue.inputValue,
                      });
                    } else {
                      setManager(newValue);
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    // Suggest the creation of a new value
                    if (params.inputValue !== '') {
                      filtered.push({
                        inputValue: params.inputValue,
                        label: `${t('Pridėti')} "${params.inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  options={_.map(employees, (employee) => ({ label: employee }))}
                  freeSolo
                  style={{ marginTop: 10 }}
                  getOptionLabel={(option) => option?.label?.toString() || option?.toString()}
                  renderInput={(params) => <TextField {...params} label={t("Darbų vadovas")} variant="outlined" />}
                  ListboxProps={{ style: { maxHeight: '15vh' } }}
                  size="small"
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </CommonDialog>
  )
}

export default WorkReportModal;